import { useContext, useEffect, useState } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { GridActionsCellItem } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Grid from "@mui/material/Grid2";
import { useDispatch, useSelector } from "react-redux";
import {
	deleteProductCategory,
	getCategoriesStatus,
	getItemCategories,
	putProductCategory,
	selectGetCategoriesById,
} from "../../redux/ProductsSlice";
import { Typography } from "@mui/material";
import ExistingCategoriesDataGrid from "../../Components/Categories/ExistingCategoriesDataGrid";
import EditCategoryDialog from "../../Components/Dialogs/EditCategoryDialog";
import LoadingOverlay from "../../Components/Loading/LoadingOverlay";
import TrimObjectStrings from "../../Helpers/TrimObjectStrings";
import { IsOnboardedContext } from "../../Contexts/IsOnboardedContext";
import { useSnackbar } from "notistack";
import { GridRowModes } from "@mui/x-data-grid";
import DeleteConfirmationDialog from "../../Components/Dialogs/DeleteConfirmationDialog";

const ManageCategories = () => {
	useEffect(() => {
		document.title = "Manage Categories - CPC";
	}, []);

	const theme = useTheme();
	const isXs = useMediaQuery(theme.breakpoints.down("sm"));

	const [chosenExistingCategories, setChosenExistingCategories] =
		useState(null);
	const [rowModesModel, setRowModesModel] = useState({});
	const [rows, setRows] = useState([]);
	const [rowToEdit, setRowToEdit] = useState(null);
	const [openDialog, setOpenDialog] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const dispatch = useDispatch();
	const existingCategories = useSelector(selectGetCategoriesById);
	const existingCategoriesStatus = useSelector(getCategoriesStatus);

	const context = useContext(IsOnboardedContext);
	const { enqueueSnackbar } = useSnackbar();

	useEffect(() => {
		if (existingCategoriesStatus === "idle") {
			dispatch(getItemCategories());
		}
	}, [dispatch, existingCategoriesStatus]);

	useEffect(() => {
		if (existingCategories && existingCategories.length > 0) {
			setRows(existingCategories);
		}
	}, [existingCategories]);

	const columns = [
		{
			field: "name",
			headerName: "Name",
			width: isXs ? 160 : 230,
			headerClassName: "new-param-theme--header",
		},
		{
			field: "createdAt",
			headerName: "Created At",
			width: isXs ? 170 : 170,
			headerClassName: "new-param-theme--header",
		},
		{
			field: "createdBy",
			headerName: "Created By",
			width: isXs ? 120 : 150,
			headerClassName: "new-param-theme--header",
		},
		{
			field: "lastModifiedAt",
			headerName: "Last Modified At",
			width: isXs ? 170 : 170,
			headerClassName: "new-param-theme--header",
		},
		{
			field: "lastModifiedBy",
			headerName: "Last Modified By",
			width: isXs ? 120 : 150,
			headerClassName: "new-param-theme--header",
		},
		{
			field: "actions",
			type: "actions",
			headerName: "Actions",
			headerClassName: "new-param-theme--header",
			width: 100,
			cellClassName: "actions",
			getActions: (category) => {
				return [
					<GridActionsCellItem
						icon={<EditIcon color='secondary' />}
						label='Edit'
						className='textPrimary'
						onClick={handleEditClick(category.id)}
						color='inherit'
					/>,
					<DeleteConfirmationDialog
						header="Delete Confirmation"
						title="Are you sure you want to delete this category?"
						description="This will remove the category from any products and quote templates."
						response={() => handleDeleteClick(category.id)}
					>
						{(showDialog) => (
							<GridActionsCellItem
								icon={<DeleteIcon color='error' />}
								label='Delete'
								onClick={showDialog}
								color='inherit'
							/>
						)}
					</DeleteConfirmationDialog>,
				];
			},
		},
	];

	const handleRowModesModelChange = (newRowModesModel) => {
		setRowModesModel(newRowModesModel);
	};

	const handleDeleteClick = (id) => {
		setIsSubmitting(true);
		let chosenCategoryIds = [id];
		if (!id) {
			chosenCategoryIds = chosenExistingCategories;
		}

		dispatch(
			deleteProductCategory(
				TrimObjectStrings({
					payload: {
						ids: [...chosenCategoryIds],
					},
				})
			)
		)
			.unwrap()
			.then(() => {
				setIsSubmitting(false);

				enqueueSnackbar("Category successfully deleted!", {
					variant: "success",
					autoHideDuration: 3000,
				});
			})
			.catch((error) => {
				let erorMessage = "Failed to delete category. Please try again.";
				if (error.status === 409) {
					erorMessage = `Failed to delete category. ${error.detail}.`;
				}

				console.error("Failed to delete category", error);
				setIsSubmitting(false);
				enqueueSnackbar(erorMessage, {
					variant: "error",
					autoHideDuration: 3000,
				});
			});

		setRowToEdit(null);
	};

	const handleEditClick = (id) => () => {
		setRowToEdit(rows.find((row) => row.id === id));
		setOpenDialog(true);
	};

	const handleClose = () => {
		setOpenDialog(false);
		setRowToEdit(null);
	};

	const handleSubmit = (data) => {
		setIsSubmitting(true);

		const id = data.id;
		const newData = {
			name: data.name,
			createdBy: data.createdBy,
			lastModifiedBy: context.userName,
		};

		dispatch(
			putProductCategory(
				TrimObjectStrings({
					id: data.id,
					payload: {
						...newData,
					},
				})
			)
		)
			.unwrap()
			.then(() => {
				setIsSubmitting(false);

				enqueueSnackbar("Category successfully updated!", {
					variant: "success",
					autoHideDuration: 3000,
				});

				handleClose();

				setRowModesModel((oldModel) => ({
					...oldModel,
					[id]: { mode: GridRowModes.View, fieldToFocus: "name" },
				}));
			})
			.catch((error) => {
				let erorMessage = "Failed to update category. Please try again.";
				if (error.status === 409) {
					erorMessage = `Failed to update category. ${error.detail}.`;
				}

				console.error("Failed to update category", error);
				setIsSubmitting(false);
				enqueueSnackbar(erorMessage, {
					variant: "error",
					autoHideDuration: 3000,
				});

				setRowModesModel((oldModel) => ({
					...oldModel,
					[id]: { mode: GridRowModes.View, fieldToFocus: "name" },
				}));
			});
	};

	return (
		<div>
			{isSubmitting && <LoadingOverlay />}
			{rowToEdit && (
				<EditCategoryDialog
					openDialog={openDialog}
					handleClose={handleClose}
					initialState={rowToEdit}
					rows={rows}
					handleSubmit={handleSubmit}
				/>
			)}
			{existingCategories ? (
				<Grid
					container
					spacing={3}
					justifyContent='space-between'
				>
					<Grid size={{ xs: 12 }}>
						<Typography variant='h2'>Manage Categories</Typography>
					</Grid>
					<Grid size={{ xs: 12 }}>
						<ExistingCategoriesDataGrid
							categories={existingCategories}
							chosenCategories={chosenExistingCategories}
							setCategory={setChosenExistingCategories}
							columns={columns}
							loading={existingCategoriesStatus === "loading"}
							isAdmin
							rowModesModel={rowModesModel}
							handleRowModesModelChange={handleRowModesModelChange}
							setRows={setRows}
							setRowModesModel={setRowModesModel}
							handleDeleteItems={handleDeleteClick}
						/>
					</Grid>
				</Grid>
			) : null}
		</div>
	);
};

export default ManageCategories;
